import { useState, useEffect } from "react";
import { useFeedback } from "../components/ActionFeedback";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "../layout/no_data.json";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";

/* ── Status config ─────────────────────────────────────── */
const statusConfig = {
  approved: {
    label: "Approved",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-400",
    border: "border-emerald-200",
  },
  pending: {
    label: "Pending",
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    border: "border-amber-200",
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-red-50",
    text: "text-red-600",
    dot: "bg-red-400",
    border: "border-red-200",
  },
};

function getStatusCfg(status) {
  return statusConfig[status] || statusConfig["pending"];
}

/* ── Avatar with gradient initials ─────────────────────── */
function AvatarInitials({ name }) {
  const initials = name
    ? name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const palettes = [
    "from-violet-500 to-purple-600",
    "from-blue-500 to-cyan-500",
    "from-emerald-500 to-teal-600",
    "from-amber-400 to-orange-500",
    "from-pink-500 to-rose-500",
  ];
  const idx = name ? name.charCodeAt(0) % palettes.length : 0;
  return (
    <div
      className={`w-11 h-11 rounded-xl bg-gradient-to-br ${palettes[idx]} flex items-center justify-center text-white text-sm font-black shadow-md flex-shrink-0`}
    >
      {initials}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════ */
export default function Registrations() {
  const { showToast, showModal, closeModal } = useFeedback();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const res = await API.get("/registrations");
      setRegistrations(res.data);
    } catch {
      showToast("Failed to fetch registrations", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ── Export CSV ─────────────────────────────────────── */
  const handleExport = () => {
    if (registrations.length === 0) return showToast("No data to export.", "error");
    showToast("Compiling registration data...", "info");
    const headers = ["Name,Email,Event ID,Status,Registration Date"];
    const rows = registrations.map((reg) => {
      const name = reg.user_id?.name || "Unknown";
      const email = reg.user_id?.email || "N/A";
      const date = new Date(reg.createdAt).toLocaleDateString();
      return `${name},${email},${reg.event_id},${reg.status},${date}`;
    });
    const blob = new Blob([headers.concat(rows).join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registrations_${Date.now()}.csv`;
    a.click();
    setTimeout(() => showToast("Export download started.", "success"), 800);
  };

  /* ── Attendee detail modal ──────────────────────────── */
  const handleAttendeeClick = (reg) => {
    const cfg = getStatusCfg(reg.status);
    showModal({
      title: "Attendee Profile",
      subtitle: "Full registration details",
      content: (
        <div className="space-y-5">
          {/* header */}
          <div className="flex items-center gap-5 p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-100">
            <AvatarInitials name={reg.name} />
            <div>
              <p className="text-lg font-black text-slate-800 tracking-tight">{reg.name}</p>
              <p className="text-xs font-bold text-slate-400 tracking-wide mt-0.5">{reg.email}</p>
              <span
                className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${cfg.bg} ${cfg.text} ${cfg.border}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
              </span>
            </div>
          </div>
          {/* detail grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Event ID</p>
              <p className="text-sm font-black text-slate-700 truncate">{reg.event}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Ticket Type</p>
              <p className="text-sm font-black text-slate-700">{reg.type}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 col-span-2">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Registered At</p>
              <p className="text-sm font-black text-slate-700">{reg.date}</p>
            </div>
          </div>
        </div>
      ),
    });
  };

  /* ── Derived stats ──────────────────────────────────── */
  const total = registrations.length;
  const approved = registrations.filter((r) => r.status === "approved").length;
  const pending = registrations.filter((r) => !r.status || r.status === "pending").length;
  const cancelled = registrations.filter((r) => r.status === "cancelled").length;

  /* ── Filtered rows ──────────────────────────────────── */
  const filtered = registrations.filter((reg) => {
    const name = (reg.user_id?.name || "").toLowerCase();
    const email = (reg.user_id?.email || "").toLowerCase();
    const eventId = (reg.event_id || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchSearch = name.includes(term) || email.includes(term) || eventId.includes(term);
    const matchStatus = statusFilter === "all" || (reg.status || "pending") === statusFilter;
    return matchSearch && matchStatus;
  });

  /* ── Animation variants ─────────────────────────────── */
  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={container}
      className="space-y-10"
    >
      {/* ── Page header ─────────────────────────────────── */}
      <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 px-1">
        <div>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Management</p>
          <h2 className="text-white text-4xl font-black tracking-tighter leading-none">
            Registrations
          </h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={handleExport}
          className="flex items-center gap-2 bg-white/10 border border-white/10 text-white px-6 py-3 rounded-2xl text-xs font-black hover:bg-white/15 transition-all uppercase tracking-widest backdrop-blur-sm"
        >
          <FileDownloadOutlinedIcon fontSize="small" /> Export CSV
        </motion.button>
      </motion.div>

      {/* ── Stat strip — puzzle-card-dark style ─────────── */}
      <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total", value: total, icon: <ListAltIcon fontSize="small" />, accent: "text-white", sub: "all registrations" },
          { label: "Approved", value: approved, icon: <CheckCircleOutlinedIcon fontSize="small" />, accent: "text-emerald-400", sub: "confirmed" },
          { label: "Pending", value: pending, icon: <HourglassEmptyIcon fontSize="small" />, accent: "text-amber-400", sub: "awaiting" },
          { label: "Cancelled", value: cancelled, icon: <CancelOutlinedIcon fontSize="small" />, accent: "text-red-400", sub: "withdrawn" },
        ].map((s) => (
          <div key={s.label} className="puzzle-card-dark flex flex-col gap-3 relative overflow-hidden group">
            {/* subtle glow */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all duration-700 pointer-events-none" />
            <div className="flex items-center justify-between">
              <span className={`${s.accent}`}>{s.icon}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">{s.sub}</span>
            </div>
            <p className={`text-4xl font-black tracking-tighter ${s.accent}`}>
              {loading ? "—" : s.value.toLocaleString()}
            </p>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* ── Table card — puzzle-card-light style ─────────── */}
      <motion.div variants={item} className="puzzle-card-light !p-0 overflow-hidden shadow-2xl border-none">

        {/* Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 w-full">
            <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" style={{ fontSize: 18 }} />
            <input
              type="text"
              placeholder="Search name, email or event ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3.5 px-5 pl-12 text-sm font-bold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0a0c10]/10 transition-all"
            />
          </div>
          {/* Filter pills */}
          <div className="flex gap-2 flex-wrap">
            {["all", "approved", "pending", "cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                  statusFilter === s
                    ? "bg-[#0a0c10] text-white border-[#0a0c10] shadow-lg"
                    : "bg-slate-50 text-slate-500 border-slate-100 hover:bg-slate-100 hover:text-slate-700"
                }`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-28 flex flex-col items-center gap-5">
            <div className="w-9 h-9 border-4 border-slate-100 border-t-[#0a0c10] rounded-full animate-spin" />
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
              Fetching registrations…
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center">
            <div className="w-44 h-44 mb-4 opacity-60">
              <Lottie animationData={noDataAnimation} loop />
            </div>
            <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">
              No registrations found
            </p>
            {(searchTerm || statusFilter !== "all") && (
              <button
                onClick={() => { setSearchTerm(""); setStatusFilter("all"); }}
                className="mt-4 text-xs font-bold text-[#0a0c10] underline underline-offset-2 hover:opacity-70 transition-opacity"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Attendee", "Event ID", "Status", "Registered"].map((h) => (
                    <th key={h} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <AnimatePresence>
                <motion.tbody
                  initial="hidden"
                  animate="visible"
                  variants={container}
                >
                  {filtered.map((reg) => {
                    const attendeeName = reg.user_id?.name || "Unknown User";
                    const attendeeEmail = reg.user_id?.email || "N/A";
                    const registrationDate = new Date(reg.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric", month: "short", year: "numeric",
                    }) + " · " + new Date(reg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    const cfg = getStatusCfg(reg.status);

                    return (
                      <motion.tr
                        key={reg._id}
                        variants={item}
                        onClick={() =>
                          handleAttendeeClick({
                            ...reg,
                            name: attendeeName,
                            email: attendeeEmail,
                            date: registrationDate,
                            event: reg.event_id,
                            type: reg.status || "Standard",
                          })
                        }
                        className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors cursor-pointer group"
                      >
                        {/* Attendee */}
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <AvatarInitials name={attendeeName} />
                            <div>
                              <p className="font-black text-slate-800 text-sm tracking-tight group-hover:text-[#0a0c10] transition-colors">
                                {attendeeName}
                              </p>
                              <p className="text-[11px] text-slate-400 font-bold mt-0.5">{attendeeEmail}</p>
                            </div>
                          </div>
                        </td>

                        {/* Event ID */}
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2.5 max-w-[180px]">
                            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                            <span className="font-bold text-slate-600 text-xs truncate">{reg.event_id}</span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-8 py-5">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${cfg.bg} ${cfg.text} ${cfg.border}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {cfg.label}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-8 py-5">
                          <p className="text-[11px] font-bold text-slate-500 whitespace-nowrap">{registrationDate}</p>
                        </td>
                      </motion.tr>
                    );
                  })}
                </motion.tbody>
              </AnimatePresence>
            </table>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-slate-50 flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Showing {filtered.length} of {total} records
              </p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Live</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
